import React, { useState, useEffect, useCallback } from 'react';
import { whTransferAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, ArrowLeftRight, CheckCircle2, X, Package } from 'lucide-react';
import { toast } from 'sonner';

export function PendingTransfersSection({ warehouseId, onTransferAccepted }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const fetchTransfers = useCallback(async () => {
    if (!warehouseId) return;
    try {
      const res = await whTransferAPI.listPending(warehouseId);
      setTransfers(res.data?.transfers || []);
    } catch {
      /* non-critical */
    } finally {
      setLoading(false);
    }
  }, [warehouseId]);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  const handleAccept = async (id) => {
    setAccepting(id);
    try {
      await whTransferAPI.accept(id);
      toast.success('Transfer accepted — inventory updated');
      fetchTransfers();
      if (onTransferAccepted) onTransferAccepted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to accept transfer');
    }
    setAccepting(null);
  };

  const handleCancel = async (id) => {
    setCancelling(id);
    try {
      await whTransferAPI.cancel(id);
      toast.success('Transfer cancelled — inventory restored to source');
      fetchTransfers();
      if (onTransferAccepted) onTransferAccepted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel transfer');
    }
    setCancelling(null);
  };

  if (loading) return null;
  if (transfers.length === 0) return null;

  return (
    <Card className="border-amber-200 dark:border-amber-800/50 bg-amber-50/30 dark:bg-amber-950/10" data-testid="pending-transfers-section">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <ArrowLeftRight className="w-4 h-4 text-amber-600" />
          <span>Pending Transfers</span>
          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs px-1.5">
            {transfers.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-2">
        {transfers.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border bg-background" data-testid={`pending-transfer-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate">{t.item_name || 'Unknown'}</span>
                <span className="text-xs font-mono bg-muted px-1 rounded">x{t.quantity}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 ml-5">
                From: <strong>{t.source_warehouse_name}</strong>
                {t.initiated_by && <> · By: {t.initiated_by}</>}
                {t.initiated_at && <> · {new Date(t.initiated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                size="sm"
                onClick={() => handleAccept(t.id)}
                disabled={accepting === t.id}
                data-testid={`accept-transfer-${t.id}`}
              >
                {accepting === t.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Accept
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCancel(t.id)}
                disabled={cancelling === t.id}
                className="text-destructive hover:text-destructive"
                data-testid={`cancel-transfer-${t.id}`}
              >
                {cancelling === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
